import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { InlineOption, InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { Locale } from "@/Common/Models/Localization/Locale";
import { BaseWorkSafeBcSubSection, IBaseWorkSafeBcSubSectionProps } from "../BaseWorkSafeBcSubSection";
import { WorkSafeBcRegularPractitionerTimeSpan } from "../Constants/WorkSafeBcRegularPractitionerTimeSpan";
import { WorkSafeBcFormValidator } from "../Models/WorkSafeBcFormValidator";

interface WorkSafeBcRegularPractitionerSectionState {
    maxWorkerDisabledDate: Date;
}

class WorkSafeBcRegularPractitionerSection extends BaseWorkSafeBcSubSection<IBaseWorkSafeBcSubSectionProps, WorkSafeBcRegularPractitionerSectionState> {
    public constructor(props: IBaseWorkSafeBcSubSectionProps) {
        super(props);

        this.state = {
            maxWorkerDisabledDate: props.model.ServiceDate
        };
    }

    public componentDidUpdate(prevProps: IBaseWorkSafeBcSubSectionProps): void {
        const maxWorkerDisabledDate = this.props.model.ServiceDate;

        if (prevProps.model.ServiceDate !== maxWorkerDisabledDate) {
            this.mutateState(s => s.maxWorkerDisabledDate = maxWorkerDisabledDate);
        }
    }

    public render(): JSX.Element {
        const data = this.props.model;
        const validator = new WorkSafeBcFormValidator(data);

        return (
            <div className="flex-grow-container subsection-border-top">
                {this.renderFields(
                    {
                        labelValueKey: "WorkSafeBc_WorkerRegularPhysicianLabel",
                        inline: true,
                        required: true,
                        isEditMode: true
                    },
                    <InlineOptionsListControl.YesOrNo
                        id={`yesOrNo_IsRegularPractitioner_${this.props.formId}`}
                        locale={Locale.en}
                        value={data.IsWorkerRegularPractitioner}
                        onChange={option => this.props.mutateModelState(s => s.IsWorkerRegularPractitioner = option?.Key)}
                        disabled={!this.props.isEditMode}
                        allowDeselect
                    />
                )}
                {this.renderFields(
                    {
                        labelValueKey: "WorkSafeBc_HowLongHasTheWOrkerBeenYourPatientLabel",
                        inline: true,
                        required: data.IsWorkerRegularPractitioner,
                        isEditMode: true
                    },
                    <InlineOptionsListControl
                        id={`HowLongHasTheWorkerBeenYourPatient_${this.props.formId}`}
                        items={this.getPractitionerTimeSpanOptions()}
                        value={data.WorkerRegularPractitionerTimeSpan}
                        onChange={option => this.props.mutateModelState(s => s.WorkerRegularPractitionerTimeSpan = option?.Key)}
                        disabled={!this.props.isEditMode}
                        allowDeselect
                    />
                )}
                {this.renderFields(
                    {
                        labelValueKey: "WorkSafeBc_AreTherePriorProblemsAffectingRecoveryLabel",
                        value: JSXHelper.BreakStringIntoParagraphs(data.PriorProblemsAffectingRecovery),
                        required: true
                    },
                    <TextAreaResizable
                        id="WorkSafeBc_AreTherePriorProblemsAffectingRecoveryField"
                        text={data.PriorProblemsAffectingRecovery}
                        className="form-control textarea-resize-vertical"
                        onChange={value => this.props.mutateModelState(s => s.PriorProblemsAffectingRecovery = value)}
                        rows={3}
                        maxRows={3}
                        maxLength={600}
                        minHeightPx={75}
                        invalid={!validator.isPriorProblemsAffectingRecoveryFieldValid()}
                    />
                )}

                <div className="row">
                    <div className="col-md-12 regular-practitioner-injuryreport">
                        {this.renderFields(
                            {
                                labelValueKey: "WorkSafeBc_IsWorkerDisabledFromLastInjuryLabel",
                                inline: true,
                                hasParentClassName: true,
                                required: true,
                                className: "margin-xl-right",
                                isEditMode: true
                            },
                            <InlineOptionsListControl.YesOrNo
                                id={`yesOrNo_IsWorkerDisabledFromLastInjuryLabel_${this.props.formId}`}
                                value={data.HasWorkerBeenDisabledFromLastReportOrInjury}
                                onChange={option => this.props.mutateModelState(s => s.HasWorkerBeenDisabledFromLastReportOrInjury = option?.Key)}
                                disabled={!this.props.isEditMode}
                                allowDeselect
                            />
                        )}
                        {this.renderFields(
                            {
                                labelValueKey: "WorkSafeBc_IfYesAsOfWhatDateLabel",
                                value: DateHelper.formatDate(data.WorkerDisabledDate, this.dateFormat),
                                inline: true,
                                hasParentClassName: true,
                                required: data.HasWorkerBeenDisabledFromLastReportOrInjury,
                                className: ""
                            },
                            <DateSelector
                                id={`IfYesAsOfWhatDate_${this.props.formId}`}
                                valid={validator.isWorkerDisabledDateValid()}
                                maxDate={this.state.maxWorkerDisabledDate}
                                onChangeCallback={date => this.props.mutateModelState(s => s.WorkerDisabledDate = date)}
                                date={data.WorkerDisabledDate}
                                dateFormat={this.dateFormatForDateSelector}
                                width={150}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private getPractitionerTimeSpanOptions(): InlineOption<WorkSafeBcRegularPractitionerTimeSpan>[] {
        return [
            new InlineOption(WorkSafeBcRegularPractitionerTimeSpan.ZeroToSixMonths, this.getString("WorkSafeBc_IsPhysicianTimeSpanOption_0to6Months")),
            new InlineOption(WorkSafeBcRegularPractitionerTimeSpan.SevenToTwelveMonths, this.getString("WorkSafeBc_IsPhysicianTimeSpanOption_7to12Months")),
            new InlineOption(WorkSafeBcRegularPractitionerTimeSpan.GreaterThanOneYear, this.getString("WorkSafeBc_IsPhysicianTimeSpanOption_GreaterThan1Year"))
        ];
    }
}

export { WorkSafeBcRegularPractitionerSection };
